import React, { useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { getProfile } from "../store/slices/authSlice";

/**
 * ProtectedRoute component to handle authentication and role-based access
 * @param {Object} props - Component props
 * @param {JSX.Element} props.children - Child components to render if authenticated
 * @param {Array} props.allowedRoles - Roles allowed to access this route
 * @param {String} props.requiredStatus - Required user status to access this route
 * @returns {JSX.Element} The protected route component
 */
const ProtectedRoute = ({
  children,
  allowedRoles = [],
  requiredStatus = null,
}) => {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use a ref to track the last time we fetched the profile
  const lastFetchRef = useRef(0);

  // Fetch the latest user profile when the component mounts, but throttle the calls
  useEffect(() => {
    if (user) {
      const now = Date.now();
      // Only fetch profile if it's been at least 30 seconds since the last fetch
      if (now - lastFetchRef.current > 30000) {
        lastFetchRef.current = now;
        dispatch(getProfile());
      }
    }
  }, [dispatch, user, location.pathname]);

  // If user is not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If roles are specified and user doesn't have the required role, redirect to home
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  // If status is required and user doesn't have the required status, redirect to approval status page
  if (requiredStatus && user.status !== requiredStatus) {
    return <Navigate to="/approval-status" replace />;
  }

  // If user is pending or rejected and trying to access a protected route that requires approval,
  // redirect to approval-status
  if (
    requiredStatus === "approved" &&
    user.role === "buyer" &&
    (user.status === "pending" || user.status === "rejected") &&
    location.pathname !== "/approval-status"
  ) {
    return <Navigate to="/approval-status" replace />;
  }

  // If user is authenticated and has the required role and status, render the children
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
  requiredStatus: PropTypes.string,
};

export default ProtectedRoute;
